/**
 * NotificationBell — F4 compact dropdown in the top nav.
 *
 * - Polls unread count every 30s + on focus.
 * - Click opens a small panel with the 10 most recent notifications.
 * - Click a notification → marks it read + navigates to action_url.
 * - Mark all read + "View all" → /notifications full page.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function timeAgo(iso) {
    if (!iso) return "";
    const t = new Date(iso).getTime();
    const diff = Date.now() - t;
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    return `${Math.floor(h / 24)}d`;
}

export default function NotificationBell() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const panelRef = useRef(null);

    const fetchCount = useCallback(async () => {
        if (!user) return;
        try {
            const { data } = await api.get("/notifications/unread-count");
            setUnread(data.unread_count || 0);
        } catch { /* silent */ }
    }, [user]);

    const fetchList = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications?limit=10");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, []);

    useEffect(() => {
        if (!user) return undefined;
        fetchCount();
        const id = setInterval(fetchCount, 30000);
        const onFocus = () => fetchCount();
        const onRefresh = () => fetchCount();
        window.addEventListener("focus", onFocus);
        window.addEventListener("notifications:refresh", onRefresh);
        return () => {
            clearInterval(id);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("notifications:refresh", onRefresh);
        };
    }, [user, fetchCount]);

    useEffect(() => {
        if (!open) return undefined;
        fetchList();
        const onClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, [open, fetchList]);

    const markRead = async (n) => {
        try {
            if (!n.read_at) {
                await api.patch(`/notifications/${n.id}/read`);
            }
            setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: x.read_at || new Date().toISOString() } : x));
            setUnread(u => Math.max(0, u - (n.read_at ? 0 : 1)));
        } catch {/* silent */}
    };

    const handleClick = async (n) => {
        await markRead(n);
        setOpen(false);
        if (n.action_url) navigate(n.action_url);
    };

    const markAllRead = async () => {
        try {
            await api.post("/notifications/mark-all-read");
            setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
            setUnread(0);
            toast.success("All notifications marked read");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="relative" ref={panelRef}>
            <button
                onClick={() => setOpen(o => !o)}
                className="p-2 rounded-md text-gray-500 hover:text-gray-800 hover:bg-gray-100 relative"
                data-testid="notifications-button"
                aria-label="Notifications"
            >
                <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
                {unread > 0 && (
                    <span
                        data-testid="notifications-unread-badge"
                        className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
                    >
                        {unread > 99 ? "99+" : unread}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-80 bg-white border border-border rounded-md shadow-lg z-40 overflow-hidden"
                    data-testid="notifications-panel"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                        <div className="text-sm font-semibold">Notifications</div>
                        <button
                            onClick={markAllRead}
                            className="text-xs text-primary hover:underline"
                            data-testid="notifications-mark-all-read"
                            disabled={unread === 0}
                        >Mark all read</button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {items.length === 0 ? (
                            <div className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
                                No notifications
                            </div>
                        ) : items.map(n => (
                            <button
                                key={n.id}
                                onClick={() => handleClick(n)}
                                className={`w-full text-left px-3 py-2 border-b border-border hover:bg-gray-50 ${n.read_at ? "" : "bg-blue-50/40"}`}
                                data-testid={`notif-${n.id}`}
                            >
                                <div className="flex items-start gap-2">
                                    {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-rose-500 shrink-0" />}
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-gray-900 truncate">{n.title}</div>
                                        {n.message && <div className="text-xs text-gray-600 truncate">{n.message}</div>}
                                        <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => { setOpen(false); navigate("/notifications"); }}
                        className="block w-full text-center py-2 text-xs text-primary hover:bg-gray-50 border-t border-border"
                        data-testid="notifications-view-all"
                    >View all notifications</button>
                </div>
            )}
        </div>
    );
}
